import { useEffect, useState } from "react";
import { portalAuthErrorMessage } from "../auth/errors";
import {
	acceptOrganizationInvitationForCurrentIdentity,
	createIdentityForOrganizationInvitation,
	loginWithPasskey,
	type OrganizationInvitationPreview,
	previewOrganizationInvitation,
} from "../auth/passkeys";
import type { PortalMeDto } from "../model";
import { StatusBanner } from "./StatusBanner";

export function OrganizationInvitationView({ onAuthenticated }: { onAuthenticated: (actor: PortalMeDto) => void }) {
	const [invitation, setInvitation] = useState<OrganizationInvitationPreview>();
	const [step, setStep] = useState<"review" | "newIdentity" | "existingIdentity">("review");
	const [existingActor, setExistingActor] = useState<PortalMeDto>();
	const [displayName, setDisplayName] = useState("");
	const [error, setError] = useState("");
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		previewOrganizationInvitation()
			.then(setInvitation)
			.catch((reason) => setError(portalAuthErrorMessage(reason, "This invitation is invalid or has expired.")));
	}, []);

	async function createIdentity() {
		if (!displayName.trim() || submitting) return;
		setSubmitting(true);
		setError("");
		try {
			onAuthenticated(await createIdentityForOrganizationInvitation(displayName.trim()));
		} catch (reason) {
			setError(portalAuthErrorMessage(reason, "Your account was not created. The invitation remains available."));
		} finally {
			setSubmitting(false);
		}
	}

	async function authenticateExistingIdentity() {
		if (submitting) return;
		setSubmitting(true);
		setError("");
		try {
			setExistingActor(await loginWithPasskey());
			setStep("existingIdentity");
		} catch (reason) {
			setError(
				portalAuthErrorMessage(reason, "Your identity could not be confirmed. The invitation remains available."),
			);
		} finally {
			setSubmitting(false);
		}
	}

	async function acceptAsExistingIdentity() {
		if (!existingActor || submitting) return;
		setSubmitting(true);
		setError("");
		try {
			onAuthenticated(await acceptOrganizationInvitationForCurrentIdentity(existingActor.id));
		} catch (reason) {
			setError(portalAuthErrorMessage(reason, "The invitation was not accepted. It remains available."));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<section className="auth-card invitation-steps" aria-labelledby="organization-invite-heading">
			<p className="eyebrow">ORGANIZATION INVITATION · STEP {step === "review" ? "1 OF 2" : "2 OF 2"}</p>
			<h1 id="organization-invite-heading" tabIndex={-1}>
				{step === "review"
					? "Review your invitation"
					: step === "newIdentity"
						? "Create your Portal identity"
						: "Confirm your Portal identity"}
			</h1>
			{error && <StatusBanner kind="error">{error}</StatusBanner>}
			{!invitation && !error && <p>Loading invitation…</p>}
			{invitation && step === "review" && (
				<>
					<p>
						<strong>{invitation.organizationName}</strong> invited{" "}
						<strong>{invitation.intendedRecipientLabel}</strong>.
					</p>
					<dl className="invitation-summary">
						<div>
							<dt>Role</dt>
							<dd>
								{invitation.offeredRole === "administrator"
									? "Administrator — all organization projects"
									: "Member"}
							</dd>
						</div>
						<div>
							<dt>Project access</dt>
							<dd>
								{invitation.allProjects
									? "All current and future organization projects"
									: invitation.projects.length
										? invitation.projects.map((project) => project.name).join(", ")
										: "No projects selected"}
							</dd>
						</div>
						<div>
							<dt>Expires</dt>
							<dd>{new Date(invitation.expiresAt).toLocaleString()}</dd>
						</div>
					</dl>
					<p>
						Previewing this page does not use the invitation. It is accepted only after you deliberately create
						and verify your passkey.
					</p>
					<div className="invitation-actions">
						<button type="button" disabled={submitting} onClick={() => void authenticateExistingIdentity()}>
							{submitting ? "Confirming identity…" : "Use my existing Portal identity"}
						</button>
						<button type="button" className="button-secondary" onClick={() => setStep("newIdentity")}>
							Create a new identity and passkey
						</button>
					</div>
				</>
			)}
			{invitation && step === "newIdentity" && (
				<>
					<p>This creates a distinct identity for you. It will not search for or merge an account by email.</p>
					<label htmlFor="organization-invite-name">Your name</label>
					<input
						id="organization-invite-name"
						value={displayName}
						maxLength={255}
						autoComplete="name"
						onChange={(event) => setDisplayName(event.target.value)}
					/>
					<button type="button" disabled={!displayName.trim() || submitting} onClick={createIdentity}>
						{submitting ? "Creating identity…" : "Create identity and passkey"}
					</button>
					<button
						type="button"
						className="button-secondary"
						disabled={submitting}
						onClick={() => setStep("review")}
					>
						Back to invitation
					</button>
				</>
			)}
			{invitation && step === "existingIdentity" && existingActor && (
				<>
					<p>
						Your passkey confirmed <strong>{existingActor.displayName}</strong>. Accepting adds that exact Portal
						identity to <strong>{invitation.organizationName}</strong>.
					</p>
					<button type="button" disabled={submitting} onClick={() => void acceptAsExistingIdentity()}>
						{submitting ? "Accepting invitation…" : `Accept as ${existingActor.displayName}`}
					</button>
					<button
						type="button"
						className="button-secondary"
						disabled={submitting}
						onClick={() => void authenticateExistingIdentity()}
					>
						Use a different passkey
					</button>
					<button
						type="button"
						className="button-secondary"
						disabled={submitting}
						onClick={() => setStep("review")}
					>
						Back to invitation
					</button>
				</>
			)}
			<a href="/login">Cancel and return to sign in</a>
		</section>
	);
}
